<template>
    <v-dialog
        v-model="dialog"
        persistent
        max-width="600px"
    >
        <v-card class="pb-6 pb-lg-0">
            <v-overlay
                :value="loading"
                absolute
            >
                <v-progress-circular
                    :size="70"
                    :width="7"
                    indeterminate
                />
            </v-overlay>

            <v-card-title class="headline text-center text-lg-left d-block d-lg-flex">
                <p class="mb-0">
                    Registration
                </p>
                <span
                    v-if="$vuetify.breakpoint.mdAndUp"
                    class="px-3"
                >
                    -
                </span>
                <span>Beneficiary Details</span>
            </v-card-title>

            <v-card-text class="pa-3 py-0">
                <v-container>
                    <v-row class="align-center justify-center">
                        <v-col class="flex-grow-0">
                            <AvatarUploadVue
                                v-model="avatar"
                                v-validate="'required'"
                                name="Avatar"
                            />
                            <v-alert
                                v-if="errors.first('Avatar')"
                                type="error"
                                outlined
                                text
                            >
                                Please upload the avatar picture
                            </v-alert>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col
                            cols="12"
                            sm="6"
                            md="6"
                        >
                            <v-text-field
                                v-model="firstname"
                                v-validate="'required'"
                                name="First Name"
                                :error-messages="errors.first('First Name')"
                                label="First Name"
                                hide-details="auto"
                                outlined
                                required
                            />
                        </v-col>

                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-text-field
                                v-model="lastname"
                                v-validate="'required'"
                                name="Last Name"
                                :error-messages="errors.first('Last Name')"
                                label="Last Name"
                                hide-details="auto"
                                required
                                outlined
                            />
                        </v-col>

                        <v-col cols="6">
                            <v-text-field
                                v-model="email"
                                v-validate="'required'"
                                name="Email Address"
                                :error-messages="errors.first('Email Address')"
                                label="Email Address"
                                hide-details="auto"
                                outlined
                                required
                            />
                        </v-col>

                        <v-col cols="6">
                            <v-select
                                v-model="relationship"
                                v-validate="'required'"
                                :items="relationships"
                                :error-messages="errors.first('Relationship')"
                                hide-details="auto"
                                label="Relationship"
                                name="Relationship"
                                outlined
                                required
                            />
                        </v-col>

                        <v-col cols="6">
                            <v-text-field
                                v-model="phone"
                                v-validate="'required|international-phone'"
                                name="Phone"
                                :error-messages="errors.first('Phone')"
                                outlined
                                label="Phone"
                                type="text"
                                hide-details="auto"
                                required
                            />
                        </v-col>

                        <v-col cols="6">
                            <v-select
                                v-model="country_abbreviation"
                                v-validate="'required'"
                                name="Country"
                                :error-messages="errors.first('Country')"
                                :items="countries"
                                color="grey darken-4"
                                item-color="primary"
                                item-text="country"
                                item-value="abbreviation"
                                label="Country"
                                hide-details="auto"
                                class="selection--teal"
                                outlined
                            />
                        </v-col>

                        <v-col cols="12">
                            <v-text-field
                                v-model="address"
                                v-validate="'required'"
                                name="Address"
                                :error-messages="errors.first('Address')"
                                label="Address"
                                hide-details="auto"
                                outlined
                                required
                            />
                        </v-col>

                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <dob-select
                                v-model="dob"
                                v-validate="'required|minimum-age'"
                                label="Date of Birth"
                                name="Date of birth"
                                hide-details="auto"
                                :error-messages="errors.first('Date of birth')"
                            />
                        </v-col>

                        <v-col
                            cols="12"
                            sm="6"
                        >
                            <v-select
                                v-model="gender"
                                v-validate="'required'"
                                :items="genders"
                                :error-messages="errors.first('Gender')"
                                hide-details="auto"
                                label="Gender"
                                name="Gender"
                                outlined
                                required
                            />
                        </v-col>

                        <v-col
                            cols="12"
                        >
                            <v-select
                                v-model="healthInsurancePlanCategory"
                                v-validate="'required'"
                                name="Health Insurance Plan Category"
                                :error-messages="errors.first('Health Insurance Plan Category')
                                    && 'Pick a Health Insurance Plan Category'"
                                :items="healthInsurancePlansCategories"
                                color="grey darken-4"
                                item-color="indigo"
                                item-text="name"
                                item-value="id"
                                label="Health Insurance Plan Category"
                                hide-details="auto"
                                class="selection--teal"
                                return-object
                                outlined
                            />
                        </v-col>

                        <v-col
                            v-if="healthInsurancePlanCategory.id"
                            cols="12"
                            class="d-flex justify-center"
                        >
                            <v-btn
                                color="primary"
                                dark
                                class="px-12"
                                @click="categoryDescriptionDialog = true"
                            >
                                View Plans
                            </v-btn>
                        </v-col>

                        <v-col
                            v-if="healthInsurancePlanCategory.id"
                            cols="12"
                        >
                            <v-select
                                v-model="health_insurance_plan_id"
                                v-validate="'required'"
                                name="Health Insurance Plan"
                                :error-messages="errors.first('Health Insurance Plan')
                                    && 'Pick a Health Insurance Plan'"
                                :items="filteredHealthInsurancePlans"
                                color="grey darken-4"
                                item-color="indigo"
                                item-text="name"
                                item-value="id"
                                label="Health Insurance Plan"
                                hide-details="auto"
                                class="selection--teal"
                                outlined
                            >
                                <template #item="{ item }">
                                    {{ item.name }} - {{ item.price }} {{ item.currency }} {{ item.type }}
                                </template>

                                <template #selection="{ item }">
                                    {{ item.name }} - {{ item.price }} {{ item.currency }} {{ item.type }}
                                </template>
                            </v-select>
                        </v-col>

                        <!-- <v-col
                            cols="12"
                            sm="4"
                        >
                            <v-autocomplete
                                :items="['AA', 'AS', 'SS']"
                                outlined
                                label="Genotype"
                                hide-details="auto"
                            />
                        </v-col>

                        <v-col
                            cols="12"
                            sm="4"
                        >
                            <v-autocomplete
                                :items="['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']"
                                outlined
                                label="Blood Group"
                                hide-details="auto"
                            />
                        </v-col> -->
                    </v-row>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn
                    color="blue darken-1"
                    text
                    @click="$emit('close')"
                >
                    Cancel
                </v-btn>
                <v-btn
                    color="blue darken-1"
                    text
                    @click="createHealthInsuranceEnrollee"
                >
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-dialog
            v-model="categoryDescriptionDialog"
            max-width="800px"
        >
            <v-card>
                <v-card-text>
                    <div v-html="healthInsurancePlanCategory.description"/>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
    /* eslint-disable camelcase */
    import AvatarUploadVue from './AvatarUpload.vue';
    import dobSelect from '../../components/dob-select.vue';

    export default {
        components: {
            AvatarUploadVue,
            dobSelect,
        },
        props: {
            dialog: Boolean,
        },
        data: () => ({
            loading: false,
            genders: ['male', 'female'],
            relationships: [
                'aunt',
                'brother',
                'child',
                'cousin',
                'father',
                'grandfather',
                'grandmother',
                'mother',
                'nephew',
                'niece',
                'sister',
                'uncle',
                'others',
            ],
            countries: [],
            healthInsuranceProviders: [],
            healthInsurancePlans: [],
            avatar: null,
            firstname: '',
            lastname: '',
            gender: null,
            dob: null,
            email: '',
            relationship: null,
            phone: '',
            country_abbreviation: '',
            address: '',
            healthInsurancePlanCategory: {},
            health_insurance_plan_id: null,
            categoryDescriptionDialog: false,
        }),
        computed: {
            healthInsuranceProvider() {
                return this.healthInsuranceProviders.find(({ slug }) => slug === 'hygeia') || {};
            },
            healthInsurancePlansCategories() {
                return this.healthInsurancePlans.map(({ healthInsurancePlanCategory }) => healthInsurancePlanCategory);
            },
            filteredHealthInsurancePlans() {
                return this.healthInsurancePlans.filter(({
                    health_insurance_provider_id,
                    health_insurance_plans_category_id,
                }) => health_insurance_provider_id === this.healthInsuranceProvider.id
                    && health_insurance_plans_category_id === this.healthInsurancePlanCategory.id);
            },
        },
        mounted() {
            const vm = this;
            function loadCountries() {
                Vue.backendApi()
                    .countries()
                    .list()
                    .then(success, error);

                function success(response) {
                    Vue.set(vm, 'countries', response.data);
                }

                function error(err) {
                    vm.$swal({
                        type: 'error',
                        title: 'Error loading countries',
                        text: err,
                    });
                }
            }

            this.loadHealthInsuranceProviders();
            this.loadHealthInsurancePlans();
            loadCountries();
        },
        methods: {
            async loadHealthInsuranceProviders() {
                try {
                    const { data } = await Vue.backendApi()
                        .healthInsuranceProviders()
                        .getAll();

                    this.healthInsuranceProviders = data;
                } catch (err) {
                    this.$swal({
                        type: 'error',
                        title: 'Error',
                        text: err,
                    });
                    throw err;
                }
            },

            async loadHealthInsurancePlans() {
                try {
                    const { data } = await Vue.backendApi()
                        .healthInsurancePlans()
                        .getAll();

                    this.healthInsurancePlans = data;
                } catch (err) {
                    this.$swal({
                        type: 'error',
                        title: 'Error',
                        text: err,
                    });
                    throw err;
                }
            },

            async createHealthInsuranceEnrollee() {
                try {
                    const valid = await this.$validator.validateAll();

                    if (!valid) {
                        return;
                    }

                    this.loading = true;

                    const {
                        avatar,
                        firstname,
                        lastname,
                        gender,
                        dob,
                        email,
                        relationship,
                        phone,
                        country_abbreviation,
                        address,
                        health_insurance_plan_id,
                    } = this;

                    const { data } = await Vue.backendApi()
                        .healthInsuranceEnrollees()
                        .create({
                            avatar,
                            firstname,
                            lastname,
                            gender,
                            dob,
                            email,
                            relationship,
                            phone,
                            country_abbreviation,
                            address,
                            health_insurance_plan_id,
                        });

                    this.$swal({
                        type: 'success',
                        title: 'Success',
                        text: 'Beneficiary saved!',
                    });

                    this.$emit('save', data);
                    this.$emit('close');

                    this.$validator.reset();
                } catch (err) {
                    this.$swal({
                        type: 'error',
                        title: 'Error',
                        text: err,
                    });
                    throw err;
                } finally {
                    this.loading = false;
                }
            },
        },
    };
</script>
